import AddIcon from '@mui/icons-material/Add'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import cloneDeep from 'lodash.clonedeep'
import { Fragment, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { LPPPetAvatar } from '@components/pet_avatar/pet_avatar'
import { Notification } from '@components/prereg_validation/notification'
import { useSessionContext } from '@providers/session'
import { i18WithParams as t } from '@shared/locale'
import { useRoutes } from 'app/use_routes'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { Hooks, Types } from 'service-api'

interface TPetCardProps {
  lpp: Types.LocationPetProfileClientDto
}

export const PetCard = ({ lpp }: TPetCardProps) => {
  const { navigateRoute } = useRoutes()

  const onClickHandler = () => navigateRoute('profilePetEdit', { lppId: lpp.id })

  return (
    <Paper key={lpp.id} elevation={1} sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between">
        <LPPPetAvatar lpp={lpp} onClick={onClickHandler} />
        <Button onClick={onClickHandler}>Edit</Button>
      </Stack>
    </Paper>
  )
}

export const MyPets = () => {
  const sessionCtx = useSessionContext()
  const { accountName: locationName = '' } = useParams()
  const { navigateRoute } = useRoutes()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))

  const lup = Hooks.useClientLocationUserProfile(locationName, sessionCtx.state.user?.lupId || '', [
    'locationPetProfiles',
    'locationPetProfiles.images.asset',
    'locationPetProfiles.locationSpecies.species',
    'locationPetProfiles.breed',
    'locationPetProfiles.profileImage.asset',
  ])

  const onAddPetHandler = () => navigateRoute('profilePetAdd', {})

  const petProfiles = useMemo(() => {
    if (lup.data?.locationPetProfiles) {
      const tmp = cloneDeep(lup.data.locationPetProfiles)
      tmp.sort((a, b) => a.displayName?.localeCompare(b.displayName || '') || 0)
      return tmp
    }
    return []
  }, [lup.data?.locationPetProfiles])

  return (
    <Box sx={{ width: '100%' }}>
      <Notification lupId={sessionCtx.state.user?.lupId || ''} />

      <Typography variant="h2" pl={isBelowMD ? 2 : 0}>
        {t('profile.pets.title')}
      </Typography>
      <Spacer height={2} />

      {lup.isLoading ? (
        <Skeleton variant="rectangular" width="100%" height={200} />
      ) : petProfiles.length === 0 ? (
        <Typography variant="body1" pl={isBelowMD ? 2 : 0}>
          {t('profile.pets.noPets')}
        </Typography>
      ) : (
        <Fragment>
          <Stack direction="column" spacing={2}>
            {petProfiles.map((lpp) => (
              <PetCard key={lpp.id} lpp={lpp} />
            ))}
          </Stack>
        </Fragment>
      )}
      <Spacer height={2} />
      <Box pl={isBelowMD ? 2 : 0}>
        <Button variant="text" startIcon={<AddIcon />} onClick={onAddPetHandler}>
          {t('profile.pets.addPet')}
        </Button>
      </Box>
    </Box>
  )
}
